import React, { JSX, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState, store } from "./redux/store";
import { CurrencyProvider, useCurrency, CsmPrice, CsmSwitcher, Elements } from "@cosmoola/react-cosmoola-js";

import { fetchAllUsers, listenForAuthChanges } from "./redux/authSlice";

/* Pages */
import MetaCommerce from "./UNIVERSAL/DASHBOARD/MetaCommerce";
import Profile from "./PROFILE/Profile";
import EditProfile from "./PROFILE/EditProfile";
import ProfileSettings from "./PROFILE/ProfileSettings";
import SettingsPrivacy from "./PROFILE/SettingsPrivacy";
import AccountSettings from "./PROFILE/AccountSettings";
import LoginSecurity from "./PROFILE/LoginSecurity";
import ShippingAddress from "./PROFILE/ShippingAddress";
import NotificationMessage from "./CLIENTDASHBOARD/MESSAGES/NotificationMessage";
import ThreadsChat from "./CLIENTDASHBOARD/MESSAGES/ThreadsChat";
import ThreadsPreview from "./CLIENTDASHBOARD/MESSAGES/ThreadsPreview";
import AuthSwitch from "./AUTH/AuthSwitch";
import MetaCommerceDashboard from "./UNIVERSAL/DASHBOARD/MetaCommerceDashboard";
import MetaCommerceCheckout from "./UNIVERSAL/DASHBOARD/MetaCommerceCheckout";
import MetaCommerceCheckoutSuccess from "./UNIVERSAL/DASHBOARD/MetaCommerceCheckoutSuccess";
import MetaClientDashboard from "./CLIENTDASHBOARD/ORDERS/MetaClientDashboard";
import MetaProductReview from "./CLIENTDASHBOARD/ORDERS/MetaProductReview";
import CommerceOnboarding from "./UNIVERSAL/DASHBOARD/CommerceOnboarding";
import MetaCommerceDetails from "./UNIVERSAL/DASHBOARD/MetaCommerceDetails";
import PaymentMethodDashboard from "./UNIVERSAL/PAY/PaymentMethodDashboard";
import PaymentMethods from "./UNIVERSAL/PAY/PaymentMethods";
import PayoutAgreement from "./UNIVERSAL/PAY/PayoutAgreement";
import PayoutsMethods from "./UNIVERSAL/PAY/PayoutsMethods";
import WireTransferOnboarding from "./UNIVERSAL/PAY/WireTransferOnboarding";
import CommerceManagementDashboard from "./UNIVERSAL/MANAGEMENT/MANAGEMENTDASHBOARD/DASHBOARD/CommerceManagementDashboard";
import HomeLive from "./UNIVERSAL/HomeLive";
import CustomerSupport from "./CLIENTDASHBOARD/CHAT/CustomerSupport";
import Messages from "./CLIENTDASHBOARD/CHAT/Messages";
import MessagesPreview from "./CLIENTDASHBOARD/CHAT/MessagesPreview";
import ResetPassword from "./AUTH/ResetPassword";
import EditPages from "./UNIVERSAL/MANAGEMENT/MANAGEMENTDASHBOARD/EDITORIAL/BLOG/EDIT/PAGES/EditPages";
import EPageContent from "./UNIVERSAL/MANAGEMENT/MANAGEMENTDASHBOARD/EDITORIAL/BLOG/EDIT/PAGES/EPageContent";
import EArticle from "./UNIVERSAL/MANAGEMENT/MANAGEMENTDASHBOARD/EDITORIAL/BLOG/EDIT/POSTS/EArticle";
import EditPost from "./UNIVERSAL/MANAGEMENT/MANAGEMENTDASHBOARD/EDITORIAL/BLOG/EDIT/POSTS/EditPosts";
import MoreNews from "./UNIVERSAL/MANAGEMENT/MANAGEMENTDASHBOARD/EDITORIAL/BLOG/EDIT/POSTS/MoreNews";
import News from "./UNIVERSAL/MANAGEMENT/MANAGEMENTDASHBOARD/EDITORIAL/BLOG/EDIT/POSTS/News";
import GeneralCommercePartnersReview from "UNIVERSAL/MANAGEMENT/MANAGEMENTDASHBOARD/GENERALCOMMERCE/GeneralCommercePartnersReview";
import MetaCheckout from "UNIVERSAL/DASHBOARD/MetaCheckout";
import Notifications from "CLIENTDASHBOARD/MESSAGES/Notifications";
import Phones from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Phones";
import Televisions from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Televisions";
import Laptops from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Laptops";
import Computers from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Computers";
import HomeAppliances from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/HomeAppliances";
import Tablets from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Tablets";
import Security from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Security";
import Cameras from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Cameras";
import Gaming from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Gaming";
import KitchenElectronics from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/KitchenElectronics";
import Networking from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Networking";
import SmartHome from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/SmartHome";
import Film from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Film";
import AudioElectronics from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Audio";
import Wearables from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Wearables";
import Accessories from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Accessories";
import CarElectronics from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/CarElectronics";
import GPS from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/GPS";
import HomeEntertainment from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/HomeEntertainment";
import OfficeElectronics from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/OfficeElectronics";
import Tools from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/Tools";
import EbookReaders from "UNIVERSAL/DASHBOARD/PRODUCTSCATEGORIES/EbookReaders";
import MetaStoreHome from "UNIVERSAL/DASHBOARD/MetaStoreHome";
import MetaStores from "UNIVERSAL/DASHBOARD/MetaStores";

/* =========================
   PROTECTED ROUTE
========================= */

const ProtectedRoute = ({ element }: { element: JSX.Element }) => {
  const { user } = useSelector((state: RootState) => state.auth);

  if (!user) {
    return <Navigate to="/AuthSwitch" replace />;
  }

  return element;
};

/* =========================
   ROUTES
========================= */

const AppRoutes = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, authLoaded } = useSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    dispatch(listenForAuthChanges());
    dispatch(fetchAllUsers());
  }, [dispatch]);

  if (!authLoaded) {
    return <div style={{ textAlign: "center", marginTop: 100 }} />;
  }

  return (
    <Routes>

      {/* ================= PUBLIC ROUTES ================= */}

      <Route path="/editpages/:pageId" element={<EditPages />} /> 
<Route path="/edit-post/:postId" element={<EditPost />} /> 
<Route path="/:slug" element={<EArticle />} /> 
<Route path="/MoreNews" element={<MoreNews />} />
 <Route path="/News" element={<News />} /> 
<Route path="/:slug" element={<EPageContent />} />


      <Route path="/" element={<HomeLive/>} />

{/* AuthSwitch is PUBLIC */}
<Route path="/AuthSwitch" element={<AuthSwitch />} />
<Route path="/ResetPassword" element={<ResetPassword />} />

{/* ================= PROTECTED ROUTES ================= */}

<Route path="/Profile/:userId" element={<ProtectedRoute element={<Profile />} />} />
<Route path="/editprofile/:userId" element={<ProtectedRoute element={<EditProfile />} />} />
<Route path="/profilesettings/:userId" element={<ProtectedRoute element={<ProfileSettings />} />} />
<Route path="/settingsprivacy/:userId" element={<ProtectedRoute element={<SettingsPrivacy />} />} />
<Route path="/accountsettings/:userId" element={<ProtectedRoute element={<AccountSettings />} />} />
<Route path="/loginsecurity/:userId" element={<ProtectedRoute element={<LoginSecurity />} />} />
<Route path="/shippingaddress" element={<ProtectedRoute element={<ShippingAddress />} />} />
<Route path="/notificationmessage/:messageId" element={<ProtectedRoute element={<NotificationMessage />} />} />


<Route path="/threadspreview" element={<ProtectedRoute element={<ThreadsPreview />} />} />
<Route path="/threadschat/:userId" element={<ProtectedRoute element={<ThreadsChat />} />} />
   
   
    <Route path="/CustomerSupport/:userId" element={<CustomerSupport />} />
    <Route path="/MessagesPreview" element={<MessagesPreview />} />
    <Route path="/Messages/:userId" element={<Messages />} />
    <Route path="/notifications/:userId" element={<Notifications />} />




<Route path="/CommerceOnboarding/:userId" element={<ProtectedRoute element={<CommerceOnboarding />} />} />
    <Route path="/CommerceManagementDashboard/:userId" element={<CommerceManagementDashboard />} />
    <Route path="/GeneralCommercePartnersReview/:brandId" element={<GeneralCommercePartnersReview />} />
<Route path="/MetaCommerce" element={<ProtectedRoute element={<MetaCommerce />} />} />
<Route path="/MetaCommerceDetails/:listingId" element={<ProtectedRoute element={<MetaCommerceDetails />} />} />
<Route path="/MetaCommerceDashboard/:userId" element={<ProtectedRoute element={<MetaCommerceDashboard />} />} />
    <Route path="/MetaCommerceCheckout/:userId" element={<ProtectedRoute element={<MetaCommerceCheckout />} />} />
      <Route path="/MetaCommerceCheckoutSuccess/:userId" element={<ProtectedRoute element={<MetaCommerceCheckoutSuccess />} />} />
            <Route path="/MetaCheckout/:userId" element={<ProtectedRoute element={<MetaCheckout />} />} />
      <Route path="/MetaClientDashboard/:userId" element={<ProtectedRoute element={<MetaClientDashboard />} />} />
            <Route path="/MetaProductReview/:guestId/:listingId/:orderId" element={<ProtectedRoute element={<MetaProductReview />} />} />
            <Route path="/MetaStores" element={<ProtectedRoute element={<MetaStores />} />} />
            <Route path="/MetaStoreHome/:brandId" element={<ProtectedRoute element={<MetaStoreHome />} />} />

            <Route path="/Phones" element={<ProtectedRoute element={<Phones />} />} />
            <Route path="/Televisions" element={<ProtectedRoute element={<Televisions />} />} />
            <Route path="/Tablets" element={<ProtectedRoute element={<Tablets />} />} />
            <Route path="/Security" element={<ProtectedRoute element={<Security />} />} />
            <Route path="/HomeAppliances" element={<ProtectedRoute element={<HomeAppliances />} />} />
            <Route path="/Computers" element={<ProtectedRoute element={<Computers />} />} />
            <Route path="/Laptops" element={<ProtectedRoute element={<Laptops />} />} />
            <Route path="/SmartHome" element={<ProtectedRoute element={<SmartHome />} />} />
            <Route path="/Networking" element={<ProtectedRoute element={<Networking />} />} />
            <Route path="/KitchenElectronics" element={<ProtectedRoute element={<KitchenElectronics />} />} />
            <Route path="/Gaming" element={<ProtectedRoute element={<Gaming />} />} />
            <Route path="/Film" element={<ProtectedRoute element={<Film />} />} />
            <Route path="/Audio" element={<ProtectedRoute element={<AudioElectronics />} />} />
            <Route path="/Cameras" element={<ProtectedRoute element={<Cameras />} />} />
            <Route path="/Wearables" element={<ProtectedRoute element={<Wearables />} />} />
            <Route path="/Tools" element={<ProtectedRoute element={<Tools />} />} />
            <Route path="/HomeEntertainment" element={<ProtectedRoute element={<HomeEntertainment />} />} />
            <Route path="/OfficeElectronics" element={<ProtectedRoute element={<OfficeElectronics />} />} />
            <Route path="/CarElectronics" element={<ProtectedRoute element={<CarElectronics />} />} />
            <Route path="/Accessories" element={<ProtectedRoute element={<Accessories />} />} />
            <Route path="/GPS" element={<ProtectedRoute element={<GPS />} />} />
            <Route path="/EbookReaders" element={<ProtectedRoute element={<EbookReaders />} />} />






{/*===============================================*/}

  <Route path="/CommerceManagementDashboard/:userId" element={<CommerceManagementDashboard />} />

  <Route path="/PayoutAgreement/:userId" element={<PayoutAgreement />} />
  <Route path="/PaymentMethodDashboard/:userId" element={<PaymentMethodDashboard />} />
  <Route path="/payoutsmethods/:userId" element={<PayoutsMethods />} />
  <Route path="/WireTransferOnboarding/:userId" element={<WireTransferOnboarding />} />
  <Route path="/PaymentMethods/:userId" element={<PaymentMethods />} />

{/* ================= FALLBACK ================= */}

<Route path="*" element={<Navigate to={user ? "/" : "/AuthSwitch"} replace />} />
    </Routes>
  );
};

/* =========================
   APP ROOT
========================= */

const App = () => (
  <Provider store={store}>
    <Router>
      <CurrencyProvider 
  apiKey={process.env.REACT_APP_COSMOOLA_PUBLISHABLE_KEY!}
>

 
      <AppRoutes />

      </CurrencyProvider>
    </Router>
  </Provider>
);

export default App;